import { ScrollArea } from '@shared/components/ui/shadcn/scroll-area';
import { CheckCircle2, MapPin, Package, ShoppingCart, Tag } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReceiptCart } from '../../hooks/useReceiptCarts';

interface CartsListProps {
    carts: ReceiptCart[];
    isLoading?: boolean;
}

export const CartsList: React.FC<CartsListProps> = ({ carts, isLoading = false }) => {
    const { t } = useTranslation('merchandiseV3');

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <p className="text-sm text-muted-foreground">
                        {t('receive.carts.loading', 'Cargando carritos...')}
                    </p>
                </div>
            </div>
        );
    }

    if (carts.length === 0) {
        return (
            <div className="flex items-center justify-center max-h-64 bg-muted/30 rounded-xl border border-dashed border-border">
                <div className="text-center p-8">
                    <ShoppingCart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                        {t('receive.carts.empty.title', 'No hay carritos utilizados')}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                        {t('receive.carts.empty.description', 'Los carritos que uses en esta recepción aparecerán aquí')}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-foreground">
                    {t('receive.carts.title', 'Carritos utilizados')}
                </h3>
                <span className="text-sm text-muted-foreground">
                    {carts.length} {carts.length === 1 ? t('receive.carts.count', 'carrito') : t('receive.carts.count_plural', 'carritos')}
                </span>
            </div>

            <ScrollArea className="h-[calc(100vh-28rem)]">
                <div className="space-y-3">
                    {carts.map((cart) => (
                        <div
                            key={cart.id}
                            className="border rounded-lg p-3 transition-colors bg-background border-border hover:border-primary/30"
                        >
                            <div className="flex items-center justify-between gap-3">
                                {/* Info del carrito */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <ShoppingCart className="w-4 h-4 text-muted-foreground" />
                                        <span className="font-mono font-semibold text-foreground">
                                            {cart.code}
                                        </span>
                                    </div>

                                    {/* Estadísticas */}
                                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                        <span className="flex items-center gap-1">
                                            <Package className="w-3 h-3" />
                                            {cart.total_products} {cart.total_products === 1 ? t('receive.carts.product', 'producto') : t('receive.carts.products_plural', 'productos')}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Tag className="w-3 h-3" />
                                            {cart.total_quantity} {t('receive.carts.units', 'ud.')}
                                        </span>
                                    </div>
                                </div>

                                {/* Estado de despacho */}
                                <div className="flex-shrink-0 text-right">
                                    {cart.has_dispatch && cart.dispatch_id ? (
                                        <div className="flex flex-col items-end gap-1">
                                            <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                                                <CheckCircle2 className="w-4 h-4" />
                                                <span className="text-xs font-medium">
                                                    {cart.dispatch_type === 'LOCATION_ORDER'
                                                        ? t('receive.carts.location_order', 'Orden de Ubicación')
                                                        : t('receive.carts.extra_work_order', 'Trabajo Extra')}
                                                </span>
                                            </div>
                                            <span className="text-xs text-muted-foreground">
                                                #{cart.dispatch_id}
                                            </span>
                                        </div>
                                    ) : (
                                        <span className="text-xs text-muted-foreground">
                                            {t('receive.carts.pending', 'Por ubicar')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
};

